import React from "react";
import { PreJobCheckListV2FormValues } from "./PreJobCheckListV2Dialog";
import { Button, Divider, Paper, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { FullScreenDialog } from "../FullScreenDialog";
import { Form, Formik } from "formik";
import { SubmitButton } from "../Buttons/SubmitButton";
import { useQuery } from "@apollo/client";
import { GET_JOB_FOR_INSPECTION } from "../../graphql/queries/getJob";
import { Query, QueryGetFilesArgs, QueryGetJobArgs, QueryGetVehicleArgs } from "../../generated/nest-graphql";
import { getValidationV2Schema } from "./validation";
import { GET_FILES } from "../../graphql/queries/getFiles";
import { FormItems, getForm } from "../Forms/utils/forms";
import { FormGenerator } from "../Forms/utils/FormGenerator";
import { GET_VEHICLE } from "../../graphql/queries/getVehicles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentAlt } from "@fortawesome/free-solid-svg-icons";
import { useAxiosClient } from "../../lib/nestBackendClient";
import { showErrorAlert, showSuccessAlert } from "../../actions";
import { useDispatch } from "../../contexts/snackbar-context";
import { AreYouSureDialog } from "../Items/AreYouSureDialog";
import { useToggle } from "../../hooks/useToggle";
import { groupServicesByGrade } from "./colorGradeHelpers";
import { default as ImmediatlyIcon } from "@material-ui/icons/Warning";
import { default as SoonIcon } from "@material-ui/icons/Report";
import { fleetJobTypes } from "../../lib/constants";
import { InspectionDraftAutosave } from "./InspectionDraftAutosave";

export const PreJobCheckListV2Form: React.FC<{
  isReadOnly?: boolean;
  initialValues: PreJobCheckListV2FormValues;
  onSubmit: (values: PreJobCheckListV2FormValues) => void | Promise<void>;
  open: boolean;
  onClose: (values: PreJobCheckListV2FormValues) => void;
  jobId: string;
}> = ({ isReadOnly, initialValues, onSubmit, open, onClose, jobId }) => {
  const dispatch = useDispatch();
  const nestBackendClient = useAxiosClient();
  const [areYouSureDialogOpen, , toggleAreYouSureDialog] = useToggle();
  const { data, refetch } = useQuery<Query, QueryGetJobArgs>(GET_JOB_FOR_INSPECTION, {
    fetchPolicy: "cache-first",
    variables: {
      id: jobId,
    },
    skip: !jobId,
  });
  const { data: vehicleData } = useQuery<Query, QueryGetVehicleArgs>(GET_VEHICLE, {
    fetchPolicy: "cache-first",
    variables: {
      id: data?.getJob?.vehicleId,
    },
    skip: !data?.getJob?.vehicleId,
  });
  const vehicle = vehicleData?.getVehicle;

  const sendDIgitalInspection = async () => {
    try {
      const response = await nestBackendClient.post("job-appointments/send-digital-inspection", {
        jobId,
        contact: data?.getJob?.contact?.id,
        phoneNumber: data?.getJob?.contact?.phoneNumber,
      });
      refetch();
      showSuccessAlert(dispatch, "Inspection sended successfully");
    } catch (error) {
      const isArray = Array.isArray(error?.response?.data?.message);
      const message = (isArray ? error?.response?.data?.message?.[0] : error?.response?.data?.message) || error.message;
      showErrorAlert(dispatch, message);
    }
  };

  const contactId = data?.getJob?.contact?.id;
  const { data: getFilesData } = useQuery<Query, QueryGetFilesArgs>(GET_FILES, {
    variables: {
      contactId: data?.getJob?.contact?.id,
    },
  });

  const { soon, immediatly } = groupServicesByGrade(data?.getJob);
  const isFleet = fleetJobTypes.includes(data?.getJob?.type);
  const isInternalMaintenance = data?.getJob?.type === "Internal Van Maintenance";
  return (
    <>
      <Formik<PreJobCheckListV2FormValues>
        validationSchema={getValidationV2Schema(data?.getJob, vehicle)}
        initialValues={initialValues}
        onSubmit={async (values) => {
          await onSubmit(values);
          await refetch();
          if (
            data.getJob.status !== "Closed" &&
            !data.getJob.inspectionReportSentAt &&
            !isFleet &&
            !isInternalMaintenance
          ) {
            toggleAreYouSureDialog();
          }
        }}
        validateOnMount
        validateOnBlur
        validateOnChange
      >
        {({ values, isValid, isSubmitting }) => {
          const form = getForm({
            job: data?.getJob,
            values,
            vehicle,
            files: getFilesData?.getFiles || [],
          });

          const finalForm = form.map((f) => ({ ...f, contactId: contactId, jobId: jobId })) as FormItems;

          return (
            <>
              <InspectionDraftAutosave jobId={jobId} enabled={open && !isReadOnly} />
              <FullScreenDialog
                title={"Inspection"}
                open={open}
                onClose={() => {
                  onClose(values);
                }}
                headerPosition={"fixed"}
              >
                <Paper className="pt-20">
                  <div className="px-4 pb-4">
                    <h2>Measurements should reflect the condition of the vehicle before NuBrakes completed repairs.</h2>
                  </div>
                  <Form>
                    <Box className="px-4 h-full">
                      <FormGenerator
                        form={finalForm}
                        usePhotoMode={isFleet || isInternalMaintenance}
                        isReadOnly={isReadOnly}
                      />
                      {!isReadOnly && (
                        <>
                          <SubmitButton isSubmitting={isSubmitting} isValid={isValid} label={"Submit"} />
                          {true && (
                            <div className="mt-4">
                              <Button
                                type="button"
                                className="uppercase w-full"
                                variant="contained"
                                color="primary"
                                startIcon={<FontAwesomeIcon icon={faCommentAlt as any} />}
                                onClick={sendDIgitalInspection}
                              >
                                Send Inspection Report Link
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </Box>
                  </Form>
                </Paper>
              </FullScreenDialog>
            </>
          );
        }}
      </Formik>
      <AreYouSureDialog
        onNo={toggleAreYouSureDialog}
        onYes={async () => {
          await sendDIgitalInspection();
          toggleAreYouSureDialog();
        }}
        header={
          <div className="flex flex-col gap-2">
            <Typography variant="h6">Inspection Summary</Typography>
            <Divider style={{ background: "#fff" }} />
            {!!immediatly?.length && (
              <>
                <div className="flex text-red-600">
                  <ImmediatlyIcon />
                  <Typography>Service Recommended Immediately</Typography>
                </div>
                <Typography>{immediatly.map((s) => s.config.title).join(", ")}</Typography>
                <Divider style={{ background: "#fff" }} />
              </>
            )}
            {!!soon?.length && (
              <>
                <div className="flex text-yellow-400">
                  <SoonIcon />
                  <Typography>Service Soon</Typography>
                </div>
                <Typography>{soon.map((s) => s.config.title).join(", ")}</Typography>
                <Divider style={{ background: "#fff" }} />
              </>
            )}
          </div>
        }
        yesButtonType="button"
        title={`Send customer a Digital Inspection Report?`}
        subTitle="Customer will receive a link via text "
        open={areYouSureDialogOpen}
        onClose={toggleAreYouSureDialog}
      />
    </>
  );
};
